import { useState } from 'react';
import { useQuoteStore, type CustomerInfo, type LineItem } from '../store/useQuoteStore';
import { useAuthStore } from '../store/useAuthStore';
import {
  fetchQuote,
  saveQuote,
  markQuoteSuperseded,
  type QuotePayload,
} from '../services/quoteService';
import { customerSchema } from '../schemas/customerSchema';
import { allocateSequenceNumber } from '../lib/sequenceAllocator';
import { resolveQuoteNumberForSave } from './quoteNumberForSave';
import type { CheckoutSummary } from '../types/checkout';

type TargetStatus = 'DRAFT' | 'SENT';

interface ValidationFailure {
  ok: false;
  message: string;
}
interface ValidationSuccess {
  ok: true;
}
type ValidationResult = ValidationSuccess | ValidationFailure;

function validateCheckout(args: {
  grandTotal: number;
  depositPercent: number | undefined;
  customer: CustomerInfo;
  items: LineItem[];
}): ValidationResult {
  if (args.grandTotal === 0) {
    return {
      ok: false,
      message: 'Cannot record an empty quotation. Please add at least one item.',
    };
  }
  // 10 is `MIN_DEPOSIT_RATIO * 100` (shared/depositGate.ts)
  // — the floor every server gate has clamped an explicit depositPercent to
  // since 2026-06-17. Keep the two numerals in step; the parity test at
  // functions/src/transitions/__tests__/depositOptionsParity.test.ts scrapes
  // this comparison and fails closed if it stops matching.
  if (args.depositPercent != null && (args.depositPercent < 10 || args.depositPercent > 100)) {
    return {
      ok: false,
      message:
        'Deposit must be between 10% and 100%, or unset to ask the customer for the full grand total.',
    };
  }
  const customerResult = customerSchema.safeParse(args.customer);
  if (!customerResult.success) {
    const issues = customerResult.error.issues;
    const firstIssue = issues[0];
    const isInstallError = issues.some(
      (i) =>
        i.path.includes('installName') ||
        i.path.includes('installMobile') ||
        i.path.includes('installPostal'),
    );
    const fieldMsg = firstIssue ? `${firstIssue.path.join('.')}: ${firstIssue.message}` : '';
    return {
      ok: false,
      message: isInstallError
        ? `Please fill in the Installation Address contact details. (${fieldMsg})`
        : `Please check the customer details — Name and Mobile are required. (${fieldMsg})`,
    };
  }
  const hasInvalidItems = args.items.some((item) => {
    if (item.source === 'adhoc') {
      // Adhoc lines need a description and a positive unit price.
      return !item.adhocDescription?.trim() || !(parseFloat(item.adhocUnitPrice ?? '') > 0);
    }
    return (
      !item.product || (item.product.pricing_method === 'Area' && (!item.widthMm || !item.heightMm))
    );
  });
  if (hasInvalidItems) {
    return {
      ok: false,
      message:
        'Some items are incomplete — catalog items need a product (and dimensions where required); adhoc items need a description and a unit price.',
    };
  }
  return { ok: true };
}

interface QuoteIdentity {
  id: string;
  version: number;
  rootQuoteId: string;
  spawnedNewVersion: boolean;
}

/** Statuses a quote holds once the customer has accepted it — it has become an
 *  order. Allow-list (not "anything but DRAFT/SENT") so dead documents
 *  (SUPERSEDED) and the pre-sale lifecycle states (ON_HOLD / LOST, both
 *  revisable) are NOT treated as orders, and any future status is non-frozen
 *  until explicitly added here. */
const FROZEN_QUOTE_STATUSES: ReadonlySet<string> = new Set([
  'CLIENT_CONFIRMED',
  'PARTIALLY_PAID',
  'FULLY_PAID',
  'PAYMENT_RECEIVED',
]);

/** A quote is "frozen" once the customer has accepted it. From CLIENT_CONFIRMED
 *  onward it becomes an order; commercial changes are order variations (Phase 2),
 *  never new quotation versions. DRAFT / SENT remain editable (negotiation), and
 *  SUPERSEDED / ON_HOLD / LOST are dead/revisable — not frozen orders. */
export function isFrozenQuoteStatus(status: string | null): boolean {
  return status != null && FROZEN_QUOTE_STATUSES.has(status);
}

/** The minimum shape the edit lock inspects. */
export interface QuoteEditLockInput {
  status?: string | null;
  paymentLedger?: unknown[] | null;
}

/**
 * Does this saved quote hold real money? ANY ledger entry counts — including a
 * QUARANTINED gateway entry on a still-SENT quote (payment-gated confirmation
 * spec §3.9), which is deliberately excluded from every *creditable* sum but is
 * still a real captured payment sitting on the document.
 *
 * This is the client-side twin of the `quoteHoldsNoMoney()` clause in
 * firestore.rules. Both the edit lock below and the dashboard's pre-sale
 * transition gate read it, so "what the UI offers" cannot drift from "what the
 * rule allows" — a disagreement surfaces to the rep as a bare "Missing or
 * insufficient permissions" (Lesson #63).
 */
export function quoteHoldsMoney(quote: QuoteEditLockInput): boolean {
  return (quote.paymentLedger?.length ?? 0) > 0;
}

/**
 * May this saved quote be opened for editing / re-versioning?
 *
 * Three ways to be locked — see `quoteEditLockReason`. Each one is a save the
 * `/quotes` rule would reject, so offering the edit would surface as "Missing
 * or insufficient permissions" (Lesson #63) rather than a readable message.
 *
 * Kept SEPARATE from isFrozenQuoteStatus deliberately: threading a ledger flag
 * through useQuoteStore / loadQuote / resetWorkspace / loadVariationDraft is
 * the stale-state trap Lessons #66/#67 record.
 *
 * SCOPE — this guards the DASHBOARD's entry points into editing a saved quote
 * (the quote-ref button and the ⋮ Edit item). It is NOT a complete gate, and
 * an earlier version of this comment wrongly claimed the dashboard was the only
 * entry point. It is not: the Zustand workspace is `persist`-wrapped, so
 * StaffDashboard's "Quote in progress → Resume" card re-enters the last-loaded
 * saved quote by navigating to /staff with no `loadQuote` and no lock check,
 * and `executeSave`'s own guard then reads the STALE `activeQuoteStatus`. A
 * quote that acquired a ledger entry after it was loaded can therefore still be
 * saved into a rule denial by that route. Not reachable until a customer can
 * PAY a SENT quote from the UI — `SENT` is already in `PAYABLE_QUOTE_STATUSES`
 * on the server; what is still missing is slice 3's "Accept & Pay Deposit"
 * CTA. Tracked as cleanup-backlog #60.
 */
export function isQuoteEditLocked(quote: QuoteEditLockInput): boolean {
  return quoteEditLockReason(quote) !== null;
}

/**
 * WHY the quote is edit-locked, or null if it isn't — so the UI can name the
 * lock instead of showing a generic one. One definition shared by every call
 * site, because two sites computing "is it locked" separately is how they drift.
 *
 * Precedence is deliberate:
 *  - `superseded` — a dead version. Saving it is a same-doc SUPERSEDED → DRAFT
 *    (`resolveQuoteIdentity` only forks off SENT), which no branch of
 *    `quoteClientStatusTransitionOk` permits.
 *  - `accepted`   — a frozen status; it is an order now, and changes go through
 *    variations. Checked before money so an accepted quote that ALSO holds
 *    money reads as an order rather than as a stray payment.
 *  - `money`      — any ledger entry, the quarantine case: a still-SENT quote
 *    holding a captured payment. Editing forks-and-supersedes and the rule
 *    denies superseding a money-holding quote.
 */
export function quoteEditLockReason(
  quote: QuoteEditLockInput,
): 'superseded' | 'accepted' | 'money' | null {
  if (quote.status === 'SUPERSEDED') return 'superseded';
  if (isFrozenQuoteStatus(quote.status ?? null)) return 'accepted';
  return quoteHoldsMoney(quote) ? 'money' : null;
}

export function resolveQuoteIdentity(args: {
  activeQuoteId: string | null;
  activeQuoteStatus: string | null;
  activeQuoteVersion: number;
  activeRootQuoteId: string | null;
  targetStatus: TargetStatus;
}): QuoteIdentity {
  // A new quotation version is spawned whenever an already-SENT quote is
  // edited — regardless of whether the rep re-saves as DRAFT ("Generate &
  // Review Quotation") or as SENT ("Skip Review & Send Immediately"). Both must
  // fork a fresh doc: a same-doc overwrite of a SENT quote is a SENT→DRAFT (or
  // frozen-totals) write that the /quotes money-safety rule denies for EVERY
  // client session, admin included, surfacing as "Missing or insufficient
  // permissions". Forking creates a new doc (a permitted `create`) and
  // supersedes the old SENT one. CLIENT_CONFIRMED (and later) quotes are frozen
  // — see isFrozenQuoteStatus — and are blocked upstream in executeSave, never
  // re-versioned here.
  const spawnsNewVersion = !!args.activeQuoteId && args.activeQuoteStatus === 'SENT';

  if (spawnsNewVersion) {
    return {
      id: crypto.randomUUID(),
      version: args.activeQuoteVersion + 1,
      rootQuoteId: args.activeRootQuoteId || (args.activeQuoteId as string),
      spawnedNewVersion: true,
    };
  }
  const id = args.activeQuoteId || crypto.randomUUID();
  return {
    id,
    version: args.activeQuoteVersion,
    rootQuoteId: args.activeRootQuoteId || id,
    spawnedNewVersion: false,
  };
}

/**
 * The quote's creation date on save — backlog #54.
 *
 * `executeSave` used to pass `createdAt: null` on EVERY save, so
 * `saveQuote`'s `?? now` fallback fired each time and an edit silently
 * overwrote the quote's true creation date with the moment of the edit
 * (observed: QTE-2026-0022 reading 02:30, its last-save time). The loaded
 * quote's original value is now carried through the store, the same way
 * `createdBy` is, and re-saved.
 *
 * A forked NEW VERSION is deliberately treated as new: it is a different
 * document with its own id, and the version list sorts on this field.
 *
 * Pure + unit-tested so the rule cannot drift.
 */
export function resolveQuoteCreatedAt(args: {
  spawnedNewVersion: boolean;
  carried: string | null;
  now: string;
}): string {
  if (args.spawnedNewVersion) return args.now;
  return args.carried ?? args.now;
}

/** Resolve who a saved quote is attributed to. A quote stays owned by the rep
 *  who created it: editing or forking a new version of an existing quote must
 *  NOT reassign authorship to whoever is currently signed in (an admin re-saving
 *  a rep's quote used to steal ownership — QTE-2026-0022). Only a brand-new
 *  quote is stamped with the current user. Pure + unit-tested so the rule can't
 *  drift. */
export function resolveQuoteAuthor(args: {
  isExistingQuote: boolean;
  existing: { createdBy: string | null; authorName: string | null; authorMobile: string | null };
  current: { email: string; fullName: string; whatsapp: string };
}): { createdBy: string; authorName: string; authorMobile: string } {
  if (args.isExistingQuote && args.existing.createdBy) {
    return {
      createdBy: args.existing.createdBy,
      authorName: args.existing.authorName ?? '',
      authorMobile: args.existing.authorMobile ?? '',
    };
  }
  return {
    createdBy: args.current.email,
    authorName: args.current.fullName,
    authorMobile: args.current.whatsapp,
  };
}

/** The frozen-quote message the rep sees. Exported so the test asserts the
 *  real string rather than a copy that can drift. */
export const FROZEN_QUOTE_SAVE_MESSAGE =
  'This quote has been accepted and is now an order. ' +
  'Changing a confirmed order will be available via Variations (coming soon).';

/**
 * What the rep is told when the LIVE quote turns out to be locked (backlog
 * #60). Each reason is a save the `/quotes` rule would reject, so the rep would
 * otherwise meet a bare `permission-denied` — or, worse, a silently successful
 * fork (see `refreshActiveQuoteStatus`).
 */
export const EDIT_LOCK_MESSAGES: Record<'superseded' | 'accepted' | 'money', string> = {
  superseded:
    'A newer version of this quote exists, so this one can no longer be edited. ' +
    'Open the latest version from the quotes list.',
  accepted: FROZEN_QUOTE_SAVE_MESSAGE,
  money:
    'A payment has landed on this quote since it was opened, so it can no longer be ' +
    'edited. Reload the quotes list and work from the order.',
};

/**
 * Everything `executeSave` checks BEFORE it writes anything — the accepted-quote
 * freeze first, then the checkout validation.
 *
 * Extracted from the body of `executeSave` (backlog #26) so the load-bearing
 * guarantee can be tested in node: a frozen quote is refused even when every
 * other field is perfectly valid, so no `saveQuote` / `allocateSequenceNumber`
 * / `markQuoteSuperseded` write is reached. The ORDER matters — freeze is
 * checked first, so a frozen quote is never told to go fix its customer
 * details as though the save were otherwise available.
 *
 * `activeQuoteId` is part of the freeze condition on purpose: a brand-new quote
 * built while a frozen one happens to be loaded in the workspace is a new
 * quote, not an edit of the frozen one.
 */
export function decideSavePreflight(args: {
  activeQuoteId: string | null;
  activeQuoteStatus: string | null;
  grandTotal: number;
  depositPercent: number | undefined;
  customer: CustomerInfo;
  items: LineItem[];
}): ValidationResult {
  if (args.activeQuoteId && isFrozenQuoteStatus(args.activeQuoteStatus)) {
    return { ok: false, message: FROZEN_QUOTE_SAVE_MESSAGE };
  }
  return validateCheckout({
    grandTotal: args.grandTotal,
    depositPercent: args.depositPercent,
    customer: args.customer,
    items: args.items,
  });
}

export interface UseQuoteCheckout {
  isGenerating: boolean;
  actionError: string | null;
  savedQuoteUrl: string | null;
  executeSave: (targetStatus: TargetStatus, summary: CheckoutSummary) => Promise<void>;
  clearError: () => void;
  dismissUrl: () => void;
}

export function useQuoteCheckout(): UseQuoteCheckout {
  const [isGenerating, setIsGenerating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [savedQuoteUrl, setSavedQuoteUrl] = useState<string | null>(null);

  const executeSave = async (targetStatus: TargetStatus, summary: CheckoutSummary) => {
    setActionError(null);

    const store = useQuoteStore.getState();

    // Re-read the saved quote before deciding anything (backlog #60). The
    // workspace is `persist`-wrapped, and StaffDashboard's "Quote in progress →
    // Resume" navigates straight back into the calculator with NO `loadQuote`,
    // so `activeQuoteStatus` can be arbitrarily stale. A stale `'SENT'` is the
    // dangerous one: `resolveQuoteIdentity` would fork a new version, the
    // fork's `create` is permitted, and the `markQuoteSuperseded` that should
    // demote v1 is denied and SWALLOWED — leaving two live versions with the
    // money on the older one and no error shown. Preventing the fork, not
    // improving the message, is the point.
    //
    // Fail CLOSED on a read failure: the rep keeps their workspace (it is
    // persisted) and can retry, which is the cheaper of the two mistakes.
    let liveStatus = store.activeQuoteStatus;
    if (store.activeQuoteId) {
      let live: QuotePayload | null;
      try {
        live = await fetchQuote(store.activeQuoteId);
      } catch {
        setActionError(
          'Could not check this quote’s current state — check your connection and try again. ' +
            'Nothing in this window is lost.',
        );
        return;
      }
      if (live) {
        const lockReason = quoteEditLockReason(live);
        if (lockReason) {
          setActionError(EDIT_LOCK_MESSAGES[lockReason]);
          return;
        }
        liveStatus = live.status;
      }
    }

    // Freeze + validation, in that order. An accepted quote has become an
    // order: editing it must not fork a new quotation version or overwrite the
    // confirmed doc — variations are the supported path. Every write below is
    // unreachable until this passes, which is what `decideSavePreflight`'s
    // tests pin (backlog #26).
    const preflight = decideSavePreflight({
      activeQuoteId: store.activeQuoteId,
      activeQuoteStatus: liveStatus,
      grandTotal: summary.grandTotal,
      depositPercent: store.depositPercent,
      customer: store.customer,
      items: store.items,
    });
    if (!preflight.ok) {
      setActionError(preflight.message);
      return;
    }

    setIsGenerating(true);
    try {
      const identity = resolveQuoteIdentity({
        activeQuoteId: store.activeQuoteId,
        // The LIVE status, not the persisted one — see the re-read above.
        activeQuoteStatus: liveStatus,
        activeQuoteVersion: store.activeQuoteVersion,
        activeRootQuoteId: store.activeRootQuoteId,
        targetStatus,
      });

      if (identity.spawnedNewVersion && store.activeQuoteId) {
        // Fire-and-forget; the new version still lands even if the demote
        // fails (e.g. permission denied in dev). markQuoteSuperseded swallows
        // its own errors internally.
        void markQuoteSuperseded(store.activeQuoteId);
      }

      // Resolve the human-readable quote number (spec 2026-09-09 numbering
      // §4.5). `resolveQuoteNumberForSave` decides: inherit (SENT+ and every
      // new version), re-letter a DRAFT whose entity changed, or allocate a
      // fresh `{L}QT-YYYY-NNNN` with the issuing-entity letter. Allocation
      // failing (network / cold start) saves without a number; the UI falls
      // back to the UUID-slice ref via displayQuoteRef. Letting the quote save
      // with a missing number beats losing the staff's work.
      const resolved = resolveQuoteNumberForSave({
        existing: store.activeQuoteNumber,
        status: store.activeQuoteStatus,
        issuedCompany: store.issuedCompany,
      });
      let quoteNumber = resolved.number;
      if (resolved.allocate) {
        try {
          const allocation = await allocateSequenceNumber('QTE', store.issuedCompany);
          quoteNumber = allocation.number;
        } catch (err) {
          console.warn(
            'Quote number allocation failed; saving without it (display falls back to UUID ref).',
            err,
          );
        }
      }

      const auth = useAuthStore.getState();
      const author = resolveQuoteAuthor({
        isExistingQuote: !!store.activeQuoteId,
        existing: {
          createdBy: store.activeQuoteCreatedBy,
          authorName: store.activeQuoteAuthorName,
          authorMobile: store.activeQuoteAuthorMobile,
        },
        current: {
          email: auth.user?.email?.toLowerCase() || 'Unknown_Staff',
          fullName: auth.profile?.fullName || '',
          whatsapp: auth.profile?.whatsapp || '',
        },
      });
      const payload: QuotePayload = {
        id: identity.id,
        customer: store.customer,
        cart: store.items,
        totals: summary,
        issuedCompany: store.issuedCompany,
        status: targetStatus,
        depositPercent: store.depositPercent,
        interiorDesign: store.interiorDesign,
        orderRemarks: store.orderRemarks.trim() || undefined,
        pepperCloudOpportunityId: store.pepperCloudOpportunityId.trim() || undefined,
        quoteNumber,
        metadata: {
          createdBy: author.createdBy,
          authorName: author.authorName,
          authorMobile: author.authorMobile,
          createdAt: resolveQuoteCreatedAt({
            spawnedNewVersion: identity.spawnedNewVersion,
            carried: store.activeQuoteCreatedAt,
            now: new Date().toISOString(),
          }),
          confirmedAt: null,
          version: identity.version,
          rootQuoteId: identity.rootQuoteId,
        },
      };

      await saveQuote(payload);

      try {
        useQuoteStore.getState().loadQuote(payload);
      } catch (storeErr) {
        // Rehydrate failure does not affect the saved quote
        console.warn(
          'Failed to rehydrate quote workspace after save; the saved quote is unaffected.',
          storeErr,
        );
      }

      // Same-origin as wherever the app is running — so a quote saved on
      // staging links to the staging domain, prod to prod, localhost to
      // localhost. Was hardcoded to the prod domain, which made every
      // staging-saved quote's preview link 404 against prod Firestore.
      setSavedQuoteUrl(`${window.location.origin}/quote/${identity.id}`);
    } catch (e) {
      console.error('Quote save failed:', e);
      const message =
        e instanceof Error
          ? e.message
          : "Couldn't save. Please check your connection and try again.";
      setActionError(message);
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    isGenerating,
    actionError,
    savedQuoteUrl,
    executeSave,
    clearError: () => setActionError(null),
    dismissUrl: () => setSavedQuoteUrl(null),
  };
}
